import { type ComponentProps, Fragment } from 'react';

import { FilterBar } from '@apps/web/components/ui/filter_bar';
import { cn } from '@apps/web/lib/utils';

import { type ConversationsInboxSortKey } from './conversations-inbox-controller';
import { useExclusiveToolbarMenus } from './conversations-inbox-exclusive-menus';
import { ConversationInboxViewOptions } from './conversations-inbox-view-options';

type FilterBarProps = ComponentProps<typeof FilterBar>;

/**
 * The two menus at the end of the filter row, Filter and Order by, with the
 * "only one open" state kept here rather than in the route: a menu toggle
 * then re-renders this pair and not every conversation row.
 */
export function ConversationInboxMenus({
    chips,
    dimensions,
    onChipsChange,
    sort,
    onSortChange,
    pillClassName,
}: {
    readonly chips: FilterBarProps['chips'];
    readonly dimensions: FilterBarProps['dimensions'];
    readonly onChipsChange: FilterBarProps['onChange'];
    readonly sort: ConversationsInboxSortKey;
    readonly onSortChange: (sort: ConversationsInboxSortKey) => void;
    readonly pillClassName?: string;
}) {
    const menus = useExclusiveToolbarMenus<'filter' | 'order'>();
    return (
        <Fragment>
            {/* display:contents wrappers carry the pointer-down guards
                without adding boxes to the flex row. */}
            <div
                className="contents"
                onPointerDownCapture={menus.guard('filter')}
            >
                <FilterBar
                    addMenuOpen={menus.isOpen('filter')}
                    chips={chips}
                    dimensions={dimensions}
                    layout="trigger"
                    onAddMenuOpenChange={menus.onOpenChange('filter')}
                    onChange={onChipsChange}
                    triggerClassName={pillClassName}
                    triggerVariant="labeled"
                />
            </div>
            <div
                className="contents"
                onPointerDownCapture={menus.guard('order')}
            >
                <ConversationInboxViewOptions
                    className={cn('ml-auto', pillClassName)}
                    onOpenChange={menus.onOpenChange('order')}
                    onSortChange={onSortChange}
                    open={menus.isOpen('order')}
                    sort={sort}
                />
            </div>
        </Fragment>
    );
}
